import { Headphones } from "lucide-react";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

const SPEED_STEPS = [1, 1.25, 1.5, 2];

/** Player de áudio com layout compacto (portal cliente + app agente). */
export function AudioAttachmentPlayer({
  src,
  label,
  className,
  compact,
}: {
  src: string;
  label?: string;
  className?: string;
  /** Menos padding (bolhas de chat estreitas) */
  compact?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [speedIndex, setSpeedIndex] = useState(0);
  const speed = SPEED_STEPS[speedIndex];

  function applySpeed(value: number) {
    if (audioRef.current) audioRef.current.playbackRate = value;
  }

  function cycleSpeed() {
    const next = (speedIndex + 1) % SPEED_STEPS.length;
    setSpeedIndex(next);
    applySpeed(SPEED_STEPS[next]);
  }

  return (
    <div
      className={cn(
        "group flex items-center gap-3 rounded-xl border border-primary/15 bg-card/95 px-3 py-2.5 shadow-sm ring-1 ring-primary/10 backdrop-blur-[1px]",
        "dark:border-primary/20 dark:bg-[#0f1b35]/90 dark:ring-primary/15",
        compact && "gap-2.5 px-2.5 py-2",
        className
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary shadow-inner ring-1 ring-primary/25",
          "dark:bg-primary/20 dark:text-primary-foreground dark:ring-primary/30",
          compact ? "h-8 w-8" : "h-10 w-10"
        )}
      >
        <Headphones className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        {label ? (
          <p className="truncate text-[11px] font-semibold leading-tight text-foreground/90">{label}</p>
        ) : null}
        <div className="flex items-center gap-1.5">
          <audio
            ref={audioRef}
            controls
            src={src}
            onLoadedMetadata={() => applySpeed(speed)}
            className={cn(
              "block w-full min-w-0 flex-1 rounded-md accent-primary",
              "[&::-webkit-media-controls-enclosure]:rounded-md [&::-webkit-media-controls-enclosure]:bg-transparent",
              "[&::-webkit-media-controls-panel]:bg-background/95 [&::-webkit-media-controls-panel]:rounded-md",
              "dark:[&::-webkit-media-controls-panel]:bg-[#16284d] dark:[&::-webkit-media-controls-current-time-display]:text-slate-100 dark:[&::-webkit-media-controls-time-remaining-display]:text-slate-300",
              compact ? "h-8" : "h-9"
            )}
            preload="metadata"
          />
          <button
            type="button"
            onClick={cycleSpeed}
            title="Velocidade de reprodução"
            className={cn(
              "shrink-0 rounded-md border border-border/60 bg-muted/40 px-1.5 font-semibold text-muted-foreground tabular-nums hover:bg-muted",
              compact ? "h-8 text-[10px]" : "h-9 text-xs"
            )}
          >
            {speed % 1 === 0 ? speed.toFixed(0) : speed}x
          </button>
        </div>
      </div>
    </div>
  );
}
